import React, { useEffect, useState } from "react";
import { Plus, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Empty } from "@/components/ui/page";
import { Pill } from "@/components/StatusBadge";
import { fmtDateTime } from "@/lib/format";

export default function Corrections() {
  const { user } = useAuth();
  const isEmp = user.role === "employee";
  const canReview = ["admin", "manager", "senior_manager"].includes(user.role);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => api.get("/corrections").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/corrections", form);
      toast.success("Correction request submitted");
      setForm(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const review = async (id, decision) => {
    try { await api.post(`/corrections/${id}/review`, { decision }); toast.success(`Request ${decision}`); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#08A7A0]";

  return (
    <div>
      <PageHeader title="Break Corrections" subtitle="Request and review break time corrections" testId="corrections-header">
        {isEmp && <button data-testid="new-correction" onClick={() => setForm({ date: new Date().toISOString().slice(0, 10), break_type: "short", actual_start: "", actual_end: "", reason: "" })} className="flex items-center gap-2 rounded-lg bg-[#08A7A0] px-4 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" /> Request Correction</button>}
      </PageHeader>

      {form && (
        <Card className="mb-4 p-5 ring-2 ring-[#08A7A0]/30" testId="correction-form">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><label className="text-xs font-semibold text-slate-500">Date</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Break Type</label><select value={form.break_type} onChange={(e) => setForm({ ...form, break_type: e.target.value })} className={`mt-1 ${inputCls}`}><option value="short">Short Break</option><option value="lunch">Lunch</option><option value="personal">Personal</option><option value="meeting">Meeting / Training</option></select></div>
            <div><label className="text-xs font-semibold text-slate-500">Actual Start</label><input type="time" value={form.actual_start} onChange={(e) => setForm({ ...form, actual_start: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Actual End</label><input type="time" value={form.actual_end} onChange={(e) => setForm({ ...form, actual_end: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
            <div className="sm:col-span-2"><label className="text-xs font-semibold text-slate-500">Reason</label><textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} className={`mt-1 ${inputCls}`} /></div>
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid="submit-correction" onClick={submit} className="rounded-lg bg-[#08A7A0] px-4 py-2 text-sm font-bold text-white">Submit</button>
            <button onClick={() => setForm(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
          </div>
        </Card>
      )}

      <Card testId="corrections-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50"><tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Employee</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Break</th>
              <th className="px-4 py-3">Requested</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Status</th>
              {canReview && <th className="px-4 py-3 text-right">Action</th>}</tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={7}><Empty label="No correction requests" /></td></tr>}
              {items.map((c) => (
                <tr key={c.id} data-testid={`correction-${c.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-[#0B1F33]">{c.employee_name}</td>
                  <td className="px-4 py-3">{c.date}</td>
                  <td className="px-4 py-3">{c.break_type}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.actual_start}–{c.actual_end}</td>
                  <td className="px-4 py-3 max-w-xs text-slate-600">{c.reason}</td>
                  <td className="px-4 py-3"><Pill tone={c.status === "approved" ? "green" : c.status === "rejected" ? "red" : "amber"}>{c.status}</Pill></td>
                  {canReview && <td className="px-4 py-3">
                    {c.status === "pending" ? (
                      <div className="flex justify-end gap-1">
                        <button data-testid={`approve-${c.id}`} onClick={() => review(c.id, "approved")} className="rounded p-1.5 text-emerald-600 hover:bg-emerald-50"><Check className="h-4 w-4" /></button>
                        <button data-testid={`reject-${c.id}`} onClick={() => review(c.id, "rejected")} className="rounded p-1.5 text-red-600 hover:bg-red-50"><X className="h-4 w-4" /></button>
                      </div>
                    ) : <span className="text-xs text-slate-400">{c.reviewed_by}</span>}
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
